import { cloneDeep } from 'lodash';
import axiosInstance from '../axios';
import { parseVendor } from './transformer';
import { IURLTopology } from '../../hook/router';
import { qlubSessionStorage } from '../../service/storage/sessionStorage';
import { timeUnix } from '../../utility/k_time';
import { apiEndpoints } from './const';
import { IGetQrDetailConfig, IGetQrDetailPayload, IPaymentQrCache, IQrDetailsResponse, IRestaurant } from './type';

interface IPromise {
    resolve: any;
    reject: any;
}

export default class VendorAPIManager {
    public static getInstance() {
        if (!this.instance) {
            this.instance = new VendorAPIManager();
        }

        return this.instance;
    }

    private static instance: VendorAPIManager;

    private singleFlightMap: { [key: string]: IPromise[] } = {};

    private qrDetailsCache: { [key: string]: IPaymentQrCache } = {};

    // cache
    private vendorCache: { [key: string]: IRestaurant } = {};

    public getVendorCache(payload: IURLTopology) {
        const key = this.getVendorKey(payload);
        if (!this.vendorCache.hasOwnProperty(key)) {
            return;
        }
        return cloneDeep(this.vendorCache[key]);
    }

    public getRestaurantInfo(payload: IURLTopology): Promise<IRestaurant> {
        const key = this.getVendorKey(payload);
        if (this.vendorCache.hasOwnProperty(key)) {
            return Promise.resolve(cloneDeep(this.vendorCache[key]));
        }
        return this.getSingleFlightVendor(payload);
    }

    public getQrDetails = (payload: IGetQrDetailPayload, config?: IGetQrDetailConfig) => {
        const url = `${payload.cc}/${payload.restaurantUnique}/${payload.tableID}/${payload.f1 || '_'}/${
            payload.f2 || '_'
        }/${payload.hash || '_'}`;
        const cacheKey = this.getQrDetailsKey(payload);
        if (config?.ignoreCache !== true && this.qrDetailsCache.hasOwnProperty(cacheKey)) {
            const cache = this.qrDetailsCache[cacheKey];
            const now = timeUnix();
            if (now - cache.time <= 300) {
                return Promise.resolve(cloneDeep(cache.details));
            }
        }
        return axiosInstance
            .Get<IQrDetailsResponse>(`${apiEndpoints.qrDetails}/${url}`, {
                'axios-retry': {
                    retries: 3,
                },
            })
            .then((res: any) => {
                return {
                    ...res.data,
                };
            })
            .then((res: IQrDetailsResponse) => {
                this.qrDetailsCache[cacheKey] = {
                    details: cloneDeep(res),
                    time: timeUnix(),
                };
                return res;
            });
    };

    private getVendorKey(payload: IURLTopology) {
        if (payload.id && payload.f1 && payload.f2 && payload.hash) {
            return `${payload.cc}_${payload.slug}/${payload.id}/${payload.f1}/${payload.f2}/${payload.hash}`;
        }
        return `${payload.cc}/${payload.slug}`;
    }

    private getSingleFlightVendor(payload: IURLTopology): Promise<IRestaurant> {
        const key = this.getVendorKey(payload);
        return new Promise((resolve, reject) => {
            if (this.singleFlightMap.hasOwnProperty(key)) {
                this.singleFlightMap[key].push({
                    resolve,
                    reject,
                });
            } else {
                this.singleFlightMap[key] = [
                    {
                        resolve,
                        reject,
                    },
                ];
                this.getVendorPr(payload)
                    .then((res) => {
                        return parseVendor(res, payload.qrMode);
                    })
                    .then((res) => {
                        this.vendorCache[key] = cloneDeep(res);
                        return res;
                    })
                    .then((res) => {
                        this.singleFlightMap[key].forEach((promise) => {
                            promise.resolve(cloneDeep(res));
                        });
                    })
                    .catch((err) => {
                        this.singleFlightMap[key].forEach((promise) => {
                            promise.reject(err);
                        });
                    })
                    .finally(() => {
                        delete this.singleFlightMap[key];
                    });
            }
        });
    }

    private getVendorPr = (payload: IURLTopology) => {
        const url =
            payload.id && payload.f1 && payload.f2 && payload.hash
                ? `${apiEndpoints.restaurant}/${payload.cc}/${payload.slug}/${payload.id}/${payload.f1}/${payload.f2}/${payload.hash}`
                : `${apiEndpoints.restaurant}/${payload.cc}/${payload.slug}`;
        const key = `qlub.vendor.cache:${url}`;
        const cacheVal = qlubSessionStorage.get(key);
        if (cacheVal) {
            try {
                const parsedVal = JSON.parse(cacheVal);
                return Promise.resolve(parsedVal);
            } catch (e) {
                console.warn('parse vendor JSON', e);
            }
        }
        return axiosInstance
            .Get<IRestaurant>(url, {
                'axios-retry': {
                    retries: 3,
                },
            })
            .then((res) => {
                qlubSessionStorage.set(key, JSON.stringify(res.data));
                return res.data;
            });
    };

    private getQrDetailsKey(payload: IGetQrDetailPayload) {
        return `${payload.cc}/${payload.restaurantUnique}/${payload.tableID}/${payload.f1 || '_'}/${
            payload.f2 || '_'
        }/${payload.hash || '_'}`;
    }
}

export * from './type';
export * from './transformer';
